import { Injectable } from "@angular/core";
import {
  HttpClient,
  HttpErrorResponse,
  HttpResponse,
  HttpResponseBase,
} from "@angular/common/http";
import { BehaviorSubject, Observable, Subscriber } from "rxjs";
import { Messages } from "../model/messages";
import { EnvironmentHelper } from "./env.helper";
import { Router } from "@angular/router";
import { AuthService } from "../auth.service";
import { Tag } from "../model/tags";

@Injectable({
  providedIn: "root",
})
export class TagsService {
  private apiUrl: string;
  private byIdUrl: string;
  public _currentTags: BehaviorSubject<Tag[]>;
  public selectedTags: BehaviorSubject<Tag | null>;

  constructor(
    private http: HttpClient,
    private env: EnvironmentHelper,
    private router: Router,
    private auth: AuthService
  ) {
    this._currentTags = new BehaviorSubject<Tag[]>([]);
    this.selectedTags = new BehaviorSubject<Tag | null>(null);
    this.apiUrl = `${env.apiBaseUrl}tag/`;
    this.byIdUrl = `${env.apiBaseUrl}tag/by-id/`;
  }

  public tags(token: string): Observable<Messages> {
    return new Observable<Messages>((observer) => {
      this.http
        .get<any>(this.apiUrl, {
          headers: { Authorization: "Bearer " + token },
          observe: "response",
        })
        .subscribe(
          (response) => {
            this.receivetags(response, observer);
          },
          (error) => {
            this.receivetags(error, observer);
          }
        );
    });
  }

  public createTags(token: string, tags: Tag): Observable<Messages> {
    return new Observable<Messages>((observer) => {
      this.http
        .post<any>(this.apiUrl, tags, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => {
            this.receivetag(response, observer);
          },
          (error) => {
            this.receivetag(error, observer);
          }
        );
    });
  }

  public updateTags(token: string, tags: Tag): Observable<Messages> {
    const url = `${this.apiUrl}by-id/${tags.id}`;

    return new Observable<Messages>((observer) => {
      if (!tags.id) {
        observer.next(Messages.from({ tags: ["tags.id.required"] }));
        return;
      }

      this.http
        .put<any>(url, tags, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receivetag(response, observer),
          (error) => this.receivetag(error, observer)
        );
    });
  }

  public deletetags(token: string, tagsId: number): Observable<Messages> {
    const url = `${this.apiUrl}by-id/${tagsId}`;
    return new Observable<Messages>((observer) => {
      this.http
        .delete<any>(url, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receivetag(response, observer),
          (error) => this.receivetag(error, observer)
        );
    });
  }

  public getTagsById(token: string, tagsId: number): Observable<Messages> {
    return new Observable<Messages>((observer) => {
      this.http
        .get<any>(`${this.byIdUrl}${tagsId}`, {
          headers: {
            Authorization: "Bearer " + token,
            "Content-type": "application/json",
          },
          observe: "response",
        })
        .subscribe(
          (response) => this.receivetag(response, observer),
          (error) => this.receivetag(error, observer)
        );
    });
  }

  private receivetags(
    response: HttpResponseBase,
    observer: Subscriber<Messages>
  ) {
    if (response.status === 200) {
      const data = (response as HttpResponse<Tag[]>)?.body;
      if (!data) {
        observer.next(Messages.from((response as HttpErrorResponse)?.error));
        return;
      }
      this._currentTags.next(data);
      observer.next(Messages.EMPTY);
    } else if (response.status === 401) {
      observer.next(Messages.from((response as HttpErrorResponse)?.error));
    } else {
      observer.next(Messages.from((response as HttpErrorResponse)?.error));
    }
  }

  private receivetag(
    response: HttpResponseBase,
    observer: Subscriber<Messages>
  ) {
    if (response.status === 200) {
      const data = (response as HttpResponse<Tag>)?.body;
      if (!data) {
        observer.next(Messages.from({ tags: ["tags.unknown"] }));
        return;
      }
      this.selectedTags.next(data);
      observer.next(Messages.EMPTY);
    } else if (response.status === 401) {
      observer.next(Messages.from({ account: ["login.not.authorized"] }));
    } else {
      observer.next(Messages.from({ tags: ["tags.incomplete"] }));
    }
  }

  public selectFromRouter() {
    const segments = this.getLastSuccessfulNavigationSegments();

    if (segments && segments.length > 0) {
      const tagId = segments[segments.length - 1];
      const token = this.auth.token;

      this.getTagsById(token, Number(tagId)).subscribe();
    } else {
      const emptyTag = new Tag(0, "", "");
      this.select(emptyTag);
    }
  }

  private getLastSuccessfulNavigationSegments(): string[] | null {
    const segments =
      this.router.lastSuccessfulNavigation?.finalUrl?.root.children["primary"]
        .segments;
    return segments ? segments.map((segment) => segment.path) : null;
  }

  public select(tags: Tag) {
    this.selectedTags.next(tags);
  }

  public get selectedTagsObservable(): Observable<Tag | null> {
    return this.selectedTags.asObservable();
  }

  public get tagsObservable(): Observable<Tag[]> {
    return this._currentTags;
  }

  public get tagsData(): Tag[] {
    return this._currentTags.value;
  }

  public get currentTags(): Tag {
    const value = this.selectedTags.value;
    return value || Tag.EMPTY;
  }
}
